import { ColDef, ColGroupDef } from '@ag-grid-community/core';

export type Column = ColDef | ColGroupDef;

export interface ColumnChooserNode {
  checked?: boolean;
  children?: ColumnChooserNode[];
  field: string;
  headerName: string;
  ignoreChildrenSelection?: boolean;
  indeterminate?: boolean;
  isCollapsed?: boolean;
  pinned?: boolean;
  root?: boolean;
}

export class ColumnChooserModel {
  private _columns: ColumnChooserNode[] = [];

  static areAllChildrenChecked(children: ColumnChooserNode[]): boolean {
    return children.every((child) => !!child.checked);
  }

  static areAllColumnsIndeterminate(columns: ColumnChooserNode[]): boolean {
    return columns.some((column) => {
      if (column.children?.length) {
        return this.areSomeChildrenChecked(column.children);
      }

      return !!column.checked;
    });
  }

  static areAllColumnsSelected(columns: ColumnChooserNode[]): boolean {
    return columns.every((column) => {
      if (column.children?.length) {
        return !!column.checked && this.areAllColumnsSelected(column.children);
      }

      return !!column.checked;
    });
  }

  static areSomeChildrenChecked(children: ColumnChooserNode[]): boolean {
    return children.some((child) => !!child.checked);
  }

  static areSomeColumnsPinned(columns: ColumnChooserNode[]): boolean {
    return columns.some((column) => {
      if (column.children?.length) {
        return this.areSomeColumnsPinned(column.children);
      }

      return !!column.pinned;
    });
  }

  protected buildTree(columnDefs: Column[]): ColumnChooserNode[] {
    return columnDefs
      .filter((col) => !!col.headerName)
      .map((col) => {
        const field = (col as ColDef).field || '';

        const isColumnGroup = ColumnChooserModel.isColumnGroup(col);

        const node: ColumnChooserNode = {
          checked: !(col as ColDef).hide || false,
          field,
          headerName: col.headerName || '',
          isCollapsed: false,
          pinned: (col as ColDef).pinned === 'left',
          root: !field,
        };

        if (isColumnGroup) {
          node.children = this.buildTree((col as ColGroupDef).children || []);

          const allChecked = ColumnChooserModel.areAllChildrenChecked(node.children);
          const someChecked = ColumnChooserModel.areSomeChildrenChecked(node.children);

          node.field = col.groupId || '';
          node.checked = allChecked;
          node.indeterminate = !allChecked && someChecked;
        }

        return node;
      });
  }

  get columns(): ColumnChooserNode[] {
    return this._columns;
  }

  set columns(columns: ColumnChooserNode[]) {
    this._columns = columns;
  }

  constructor(protected columnDefs: Column[]) {
    this.columns = this.buildTree(columnDefs);
  }

  static isColumnGroup(col: Column): col is ColGroupDef {
    return 'children' in col;
  }
}
